import React from 'react'
import { createRoot } from 'react-dom/client'
import { StateProvider } from '@State'
import { AssessmentCanvas } from './components/AssessmentCanvas'
import { transformQuestionData } from './questionDataHandlers'

function transformQuestion(q) {
  const stimulus = q.stimulus || ''
  const hasTabs = q.tabs && q.tabs.length > 0
  // q.text is serialized as _h(stimulus) + _h(body) in submission_serializer.py,
  // so startsWith reliably strips the stimulus prefix for tabbed questions.
  const stimulusInText = stimulus && hasTabs && q.text.startsWith(stimulus)
  if (stimulus && hasTabs && !stimulusInText) {
    console.warn(`[assessment_canvas] stimulus/text mismatch on question ${q.guid} — stimulus may render twice`)
  }
  const text = stimulusInText ? q.text.slice(stimulus.length) : q.text
  return {
    guid: q.guid,
    type: q.type,
    text: text,
    stimulus: hasTabs ? stimulus : '',
    points: q.points,
    tabs: q.tabs,
    answer: q.answer || null,
    gradePoints: q.grade_points,
    isAnswered: q.is_answered,

    correctAnswer: q.correct_answer || null,
    isCorrect: q.is_correct !== undefined ? q.is_correct : null,
    feedback: q.feedback || '',
    fullyAnswered: false,
    ngnCasesetSubmitted: q.ngn_caseset_submitted || false,
    batchStyle: q.batch_style ? {
      backgroundColor: q.batch_style.background_color,
      borderColor: q.batch_style.border_color,
      textColor: q.batch_style.text_color,
    } : null,
    data: transformQuestionData(q.type, q.data),
  }
}

function buildInitialState(props) {
  return {
    activitySubmissionId: props.activity_submission_id,
    coursenum: props.coursenum,
    readonly: props.readonly,
    showFeedback: props.show_feedback,
    questions: props.questions.map(q => {
      if (q.type === 'caseset') {
        return {
          guid: q.guid,
          type: 'caseset',
          title: q.title || '',
          stimuli: q.stimuli || '',
          batchStyle: q.batch_style ? {
            backgroundColor: q.batch_style.background_color,
            borderColor: q.batch_style.border_color,
            textColor: q.batch_style.text_color,
          } : null,
          data: {},
          questions: (q.questions || []).map(transformQuestion),
        }
      }
      return transformQuestion(q)
    }),
    savingGuids: new Set(),
    submitStatus: 'idle', // 'idle' | 'submitting' | 'submitted' | 'error'
    feedbackSettings: {
      showPoints: (props.feedback_settings?.feedback_points || '') !== 'hide',
      // 'show' = always, 'incorrect' = only when incorrect, 'hide' = never
      feedbackDisplay: props.feedback_settings?.feedback_display || 'show',
      showAnswer: (props.feedback_settings?.feedback_answer || '') !== 'hide',
      feedbackTiming: props.feedback_settings?.feedback_timing || '',
      showFeedbackLinks: props.feedback_settings?.show_feedback_links ?? false,
      enableAiClarify: props.feedback_settings?.enable_ai_clarify ?? false,
    },
  }
}

function mapQuestionsDeep(questions, guid, updater) {
  return questions.map(q => {
    if (q.guid === guid) return updater(q)
    if (q.type === 'caseset' && q.questions) {
      const newSubQs = q.questions.map(sq => sq.guid === guid ? updater(sq) : sq)
      if (newSubQs.some((sq, i) => sq !== q.questions[i])) {
        return { ...q, questions: newSubQs }
      }
    }
    return q
  })
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_ANSWER':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({ ...q, answer: action.answer })),
      }

    case 'SAVE_START': {
      const saving = new Set(state.savingGuids)
      saving.add(action.guid)
      return { ...state, savingGuids: saving }
    }

    case 'SAVE_SUCCESS': {
      const saving = new Set(state.savingGuids)
      saving.delete(action.guid)
      const p = action.payload
      return {
        ...state,
        savingGuids: saving,
        showFeedback: p.show_feedback !== undefined ? p.show_feedback : state.showFeedback,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({
          ...q,
          isAnswered: true,
          gradePoints: p.grade_points !== undefined ? p.grade_points : q.gradePoints,
          isCorrect: p.is_correct !== undefined ? p.is_correct : q.isCorrect,
          correctAnswer: p.correct_answer ?? q.correctAnswer,
          feedback: p.feedback ?? q.feedback,
        })),
      }
    }

    case 'SAVE_ERROR': {
      const saving = new Set(state.savingGuids)
      saving.delete(action.guid)
      return { ...state, savingGuids: saving }
    }

    case 'SET_FULLY_ANSWERED':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({ ...q, fullyAnswered: action.value })),
      }

    case 'SET_CASESET_SUBMITTED':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({ ...q, ngnCasesetSubmitted: true })),
      }

    case 'FILE_UPLOAD_START':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({
          ...q, data: { ...q.data, uploadingFile: action.filename, uploadProgress: 0, uploadError: null },
        })),
      }

    case 'FILE_UPLOAD_PROGRESS':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({
          ...q, data: { ...q.data, uploadProgress: action.percent },
        })),
      }

    case 'FILE_UPLOAD_SUCCESS':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({
          ...q,
          isAnswered: action.isAnswered,
          gradePoints: action.gradePoints !== undefined ? action.gradePoints : q.gradePoints,
          data: {
            ...q.data,
            files: [...q.data.files, action.file],
            uploadingFile: null,
            uploadProgress: 0,
            uploadError: null,
          },
        })),
      }

    case 'FILE_UPLOAD_ERROR':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({
          ...q, data: { ...q.data, uploadingFile: null, uploadProgress: 0, uploadError: action.error },
        })),
      }

    case 'FILE_DELETE_SUCCESS':
      return {
        ...state,
        questions: mapQuestionsDeep(state.questions, action.guid, q => ({
          ...q,
          isAnswered: action.isAnswered,
          gradePoints: action.gradePoints !== undefined ? action.gradePoints : q.gradePoints,
          data: {
            ...q.data,
            files: q.data.files.filter(f => f.id !== action.fileId),
          },
        })),
      }

    case 'SUBMIT_START':
      return { ...state, submitStatus: 'submitting' }

    case 'SUBMIT_SUCCESS':
      return { ...state, submitStatus: 'submitted' }

    case 'SUBMIT_ERROR':
      return { ...state, submitStatus: 'error' }

    default:
      return state
  }
}

export const startAssessmentCanvas = (container, initialProps) => {
  const initialState = buildInitialState(initialProps)
  const root = createRoot(container)
  root.render(
    <StateProvider initialState={initialState} reducer={reducer}>
      <AssessmentCanvas />
    </StateProvider>
  )
}
